import {Event} from '@/apiTypes/event';
import {PrerequisiteStatus} from '@/apiTypes/registration';
import {CollapsibleRichText} from '@/components/richTextBlock/collapsibleRichText';
import {Button} from '@/components/ui/button';
import {Card, CardContent} from '@/components/ui/card';
import {useAnalytics} from '@/hooks/useAnalytics';
import {Link} from '@tanstack/react-router';
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle,
  ChevronRight,
  ExternalLink,
  Info,
  Pencil,
} from 'lucide-react';
import {FC, useCallback} from 'react';
import {Typography} from '../ui/typography';
import {EventSection} from '../eventPage/eventSection';

/** Displays the event organizer with their logo, name, and verified status. */
export function HostedByCard({event}: {event: Event}) {
  if (!event.organizer) {
    return null;
  }

  const {organizer} = event;

  return (
    <div className="flex justify-between items-center">
      <div className="flex flex-row items-center gap-3 flex-1">
        {organizer.logoUrl ? (
          <img
            src={organizer.logoUrl}
            alt={organizer.name}
            className="w-14 h-14 rounded-full object-cover"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
            <span className="text-xl font-semibold text-gray-400">
              {organizer.name.charAt(0)}
            </span>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-500">Hosted by</p>
          <div className="flex items-center gap-1.5">
            <Typography level="S2" className="group-hover:underline">
              {organizer.name}
            </Typography>
            {organizer.isVerified && (
              <BadgeCheck className="w-5 h-5 text-blue-500" />
            )}
          </div>
        </div>
      </div>
      <Button variant="outline" asChild>
        <Link
          to="/organizer/$organizerId"
          params={{organizerId: organizer.id}}
          className="group flex items-center gap-4"
        >
          View profile
          <ChevronRight />
        </Link>
      </Button>
    </div>
  );
}

/** About section with event description and contribute CTA. */
export function AboutSection({
  event,
}: {
  event: Event;
  isClaimable: boolean;
}) {
  const {track} = useAnalytics();

  const handleExpand = useCallback(() => {
    track('About Section Expanded', {
      event_id: event.id,
      event_name: event.name,
    });
  }, [track, event.id, event.name]);

  return (
    <EventSection label="About">
      {event.description ? (
        <CollapsibleRichText
          html={event.description}
          className="text-gray-600 text-sm leading-relaxed [&_p]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-gray-900 [&_h4]:mb-1"
          onExpand={handleExpand}
        />
      ) : (
        <p className="text-gray-400 text-sm italic mb-4">
          No description available for this event yet.
        </p>
      )}
    </EventSection>
  );
}

/**
 * Displays prerequisite events that a rider must have completed before
 * registering. Hidden when there are no prerequisites. Card colour reflects
 * whether the authenticated user has satisfied all requirements.
 * Guests see the requirement list without a satisfaction indicator.
 */
export function PrerequisitesCard({
  prerequisites,
  allSatisfied,
}: {
  prerequisites: PrerequisiteStatus['prerequisites'];
  allSatisfied: PrerequisiteStatus['allSatisfied'];
}) {
  if (prerequisites.length === 0) {
    return null;
  }

  const isSingle = prerequisites.length === 1;
  const isSatisfied = allSatisfied === true;
  const isLoggedInUnsatisfied = allSatisfied === false;

  const cardClass = isSatisfied
    ? 'border border-green-200 bg-green-50 shadow-sm'
    : 'border border-blue-200 bg-blue-50 shadow-sm';

  const iconCircleClass = isSatisfied
    ? 'w-10 h-10 shrink-0 rounded-full bg-green-500 flex items-center justify-center'
    : 'w-10 h-10 shrink-0 rounded-full bg-blue-500 flex items-center justify-center';

  const heading = isSatisfied
    ? isSingle
      ? 'Requirement Met'
      : 'Requirements Met'
    : isSingle
      ? 'Registration Requirement'
      : 'Registration Requirements';

  const footerNote = (
    <p className="text-sm text-gray-600 mt-3">
      If you completed this requirement outside of Wayra, you can still register
      and the organizer will manually verify your eligibility.
    </p>
  );

  return (
    <Card className={cardClass}>
      <CardContent className="px-5">
        <div className="flex items-start gap-3">
          <div className={iconCircleClass}>
            {isSatisfied ? (
              <CheckCircle className="w-5 h-5 text-white" />
            ) : (
              <AlertCircle className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold text-gray-900 mb-1">{heading}</h3>
            {isSingle ? (
              <p className="text-sm text-gray-600">
                To register for this event, participants must have completed
                <br />
                <Link
                  to="/event/$eventId"
                  params={{eventId: prerequisites[0].id}}
                  className="font-bold text-gray-900 hover:underline"
                >
                  {prerequisites[0].name}
                </Link>
                .
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                To register for this event, participants must have completed:
              </p>
            )}

            {isSingle ? (
              <>
                <Link
                  to="/event/$eventId"
                  params={{eventId: prerequisites[0].id}}
                  className="inline-flex self-start items-center gap-2 mt-1 px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Required Event
                </Link>
                {isLoggedInUnsatisfied && footerNote}
              </>
            ) : (
              <>
                <ul className="space-y-1.5 ml-13 pl-1">
                  {prerequisites.map((prereq) => (
                    <li
                      key={prereq.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      {prereq.satisfied === true && (
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      )}
                      {prereq.satisfied === false && (
                        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                      )}
                      <Link
                        to="/event/$eventId"
                        params={{eventId: prereq.id}}
                        className="font-medium text-gray-900 hover:underline"
                      >
                        {prereq.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                {isLoggedInUnsatisfied && (
                  <p className="text-xs text-gray-500 mt-3 ml-12">
                    If you completed this requirement outside of Wayra, your
                    registration may not be reflected here.
                  </p>
                )}
              </>
            )}
          </div>{' '}
        </div>
      </CardContent>
    </Card>
  );
}

/** Placeholder card shown when event data is incomplete. */
interface MissingInfoCardProps {
  onClick: () => void;
}

export const MissingInfoCard: FC<MissingInfoCardProps> = ({onClick}) => (
  <Card className="border border-gray-200 shadow-sm">
    <CardContent className="px-5 text-center flex flex-col gap-4">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
        <Info className="w-6 h-6 text-gray-400" />
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <Typography level="S2">Missing Route Files or Details?</Typography>
          <Typography level="B1">
            This event page is still being completed. If you have GPX files,
            course maps, parking info, or photos, help us make it better.
          </Typography>
        </div>
        <Button onClick={onClick} variant="outline" size="sm">
          <Pencil className="w-4 h-4 mr-1.5" />
          Submit Information
        </Button>
      </div>
    </CardContent>
  </Card>
);
